import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { PenLine, Target, ArrowRight, X, Check } from 'lucide-react';
import { usePageTitle } from '../lib/usePageTitle';
import { ApiError, publicApi } from '../lib/apiClient';
import { textError, emailError, urlError } from '../lib/publicFormValidation';

const benefits = [
  {
    title: 'Reframing the story',
    description:
      "We help you position founder experience for the specific role you're targeting — what to lead with, and what to leave in the background.",
    icon: PenLine,
  },
  {
    title: 'The right seniority match',
    description:
      'We match you against roles where founder-level ownership is actually an asset — not roles that will quietly resent a founder background.',
    icon: Target,
  },
  {
    title: 'Direct to decision-makers',
    description:
      'Our recruiters advocate your background directly to hiring leaders, rather than letting a resume get filtered out by a keyword-matching screen.',
    icon: ArrowRight,
  },
];

const steps = [
  {
    number: '01',
    title: 'Submit your profile and your story',
    description:
      "Tell us what you built, why you're moving on, and the kind of operating role you're aiming for.",
  },
  {
    number: '02',
    title: 'A reframing session',
    description:
      'We work through your founder experience together and translate it into language hiring teams recognize.',
  },
  {
    number: '03',
    title: 'Matched to the right seniority',
    description:
      'We target roles where your level of ownership reads as an asset, not a liability.',
  },
  {
    number: '04',
    title: 'Direct advocacy',
    description:
      'Our recruiters put your story in front of the actual decision-maker, not a keyword filter.',
  },
];

interface FormErrors {
  name?: string;
  email?: string;
  linkedin?: string;
  pitch?: string;
}

// LinkedIn profile/company URL validator
const isLinkedInUrl = (value: string): boolean => {
  try {
    const trimmed = value.trim();
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(withProtocol);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    return (
      (host === 'linkedin.com' || host.endsWith('.linkedin.com')) &&
      parsed.pathname.length > 1
    );
  } catch {
    return false;
  }
};

// Name validator - only alphabets, spaces, apostrophes, hyphens and periods allowed
const isAlphabeticName = (value: string): boolean => {
  return /^[A-Za-z\s'.-]+$/.test(value.trim());
};

export const ExFoundersPage: React.FC = () => {
  usePageTitle('Roles for Ex-Founders | QuantumHire');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [pitch, setPitch] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const [errors, setErrors] = useState<FormErrors>({});

  const openModal = () => {
    setSubmitError(null);
    setIsModalOpen(true);
    setIsSubmitted(false);
    setErrors({});
  };

  const closeModal = () => {
    if (submittingRef.current) return;
    setIsModalOpen(false);
    setIsSubmitted(false);
    setErrors({});
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Enter your name.';
    }

    if (!email.trim()) {
      newErrors.email = 'Enter your email.';
    }

    if (!linkedin.trim()) {
      newErrors.linkedin = 'Enter your LinkedIn profile URL.';
    }

    if (!pitch.trim()) {
      newErrors.pitch = 'Tell us what you can solve — a sentence or two is enough.';
    }

    newErrors.name ||= textError(name, 'Name', 2, 100);
    if (!newErrors.name && name.trim() && !isAlphabeticName(name)) {
      newErrors.name = 'Name should only contain alphabets.';
    }
    newErrors.email ||= emailError(email, 255);
    newErrors.linkedin ||= urlError(linkedin, 500);
    if (!newErrors.linkedin && linkedin.trim() && !isLinkedInUrl(linkedin)) {
      newErrors.linkedin = 'Enter a valid LinkedIn profile URL (e.g. linkedin.com/in/yourname).';
    }
    newErrors.pitch ||= textError(pitch, 'Comments', 10, 5000);
    setErrors(newErrors);

    return Object.values(newErrors).every((error) => !error);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current) return;

    if (!validateForm()) {
      return;
    }

    if (file) {
      const fileError = file.size === 0 ? 'Resume must not be empty.'
        : file.size > 5 * 1024 * 1024 ? 'Resume must be 5 MB or smaller.'
        : !['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)
          ? 'Resume must be a PDF, DOC, or DOCX file.' : null;
      if (fileError) { setSubmitError(fileError); return; }
    }
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await publicApi.submitExFounderInquiry({
        name,
        email,
        linkedin_url: linkedin,
        comments: pitch,
      }, file);
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f6f2] font-['Inter',sans-serif]">
      {/* HERO */}

      <header className="bg-[linear-gradient(135deg,#eef0eb_0%,#F6EFDD_55%,#eef0eb_100%)] px-6 pt-28 sm:px-8 sm:pt-32">
        <div className="mx-auto grid max-w-[1180px] items-center gap-10 pb-14 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            <div className="mb-6 inline-flex items-center gap-2 border border-[rgba(138,100,21,0.25)] px-3 py-[7px] font-['JetBrains_Mono',monospace] text-[11.5px] uppercase text-[#8A6415]">
              <span className="h-2 w-2 rounded-full bg-[#D6A039]" />
              QuantumHire for Ex-Founders
            </div>

            <h1 className="max-w-[700px] font-['Poppins',sans-serif] text-[clamp(32px,4.6vw,52px)] font-semibold leading-[1.1] tracking-[-0.02em] text-[#14150f]">
              You ran a company. Let&apos;s translate that into your{' '}
              <em className="not-italic text-[#8A6415]">next</em> role.
            </h1>

            <p className="mt-5 max-w-[640px] text-[17px] leading-[1.6] text-[#4d5149]">
              Moving from founder to operator inside someone else&apos;s company is a real
              transition — the skills are there, but the resume and the story usually aren&apos;t
              built for it yet. We work specifically with ex-founders re-entering as operators and
              leaders.
            </p>

            <div className="mt-8 flex justify-center lg:justify-start">
              <button
                type="button"
                onClick={openModal}
                className="inline-flex items-center justify-center bg-[#D6A039] px-[26px] py-[15px] font-['Inter',sans-serif] text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-[#8A6415]"
              >
                Submit your profile →
              </button>
            </div>
          </motion.div>

          {/* IMAGE COLLAGE */}

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mx-auto w-full"
          >
            <div className="mx-auto grid h-[440px] max-w-[580px] grid-cols-[1.3fr_1fr_1fr] grid-rows-2 gap-3">
              <div className="row-span-2 overflow-hidden rounded-[16px] shadow-[0_10px_26px_rgba(20,21,15,0.14)]">
                <img
                  src="https://images.unsplash.com/photo-1624797432677-6f803a98acb3?auto=format&fit=crop&w=900&q=80"
                  alt=""
                  className="h-full w-full object-cover object-center"
                />
              </div>

              <div className="overflow-hidden rounded-[16px] shadow-[0_10px_26px_rgba(20,21,15,0.14)]">
                <img
                  src="https://images.unsplash.com/photo-1574842537689-5407308d37bb?auto=format&fit=crop&w=700&q=80"
                  alt=""
                  className="h-full w-full object-cover object-center"
                />
              </div>

              <div className="overflow-hidden rounded-[16px] shadow-[0_10px_26px_rgba(20,21,15,0.14)]">
                <img
                  src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=700&q=80"
                  alt=""
                  className="h-full w-full object-cover object-center"
                />
              </div>

              <div className="overflow-hidden rounded-[16px] shadow-[0_10px_26px_rgba(20,21,15,0.14)]">
                <img
                  src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=700&q=80"
                  alt=""
                  className="h-full w-full object-cover object-center"
                />
              </div>

              <div className="overflow-hidden rounded-[16px] shadow-[0_10px_26px_rgba(20,21,15,0.14)]">
                <img
                  src="https://images.unsplash.com/photo-1613181013804-1dcba09e6a9d?auto=format&fit=crop&w=700&q=80"
                  alt=""
                  className="h-full w-full object-cover object-center"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* WHY */}

      <section className="px-6 py-20 sm:px-8">
        <div className="mx-auto max-w-[1180px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-12 max-w-[760px] text-center"
          >
            <h2 className="font-['Poppins',sans-serif] text-[clamp(24px,3.2vw,36px)] font-semibold leading-[1.2] tracking-[-0.02em] text-[#14150f]">
              Why this is a distinct search
            </h2>

            <p className="mt-4 text-[15.5px] leading-[1.6] text-[#68716d]">
              Hiring teams often don&apos;t know how to read a founder resume — too much ownership
              can read as unfocused, and the wins are hard to benchmark against a normal career
              ladder.
            </p>
          </motion.div>

          <div className="grid gap-5 md:grid-cols-3">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;

              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.08,
                  }}
                  className="rounded-[8px] border-2 border-[#D6A039]/30 bg-[#f5f6f2] p-7"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-[12px] bg-[#F6EFDD]">
                    <Icon size={28} strokeWidth={2.2} className="text-[#8A6415]" />
                  </div>

                  <h3 className="font-['Poppins',sans-serif] text-[18px] font-semibold leading-[1.3] tracking-[-0.02em] text-[#14150f]">
                    {benefit.title}
                  </h3>

                  <p className="mt-3 text-[14px] leading-[1.6] text-[#68716d]">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}

      <section className="border-t border-black/5 px-6 py-20 sm:px-8">
        <div className="mx-auto max-w-[900px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 text-center"
          >
            <h2 className="font-['Poppins',sans-serif] text-[clamp(24px,3.2vw,36px)] font-semibold leading-[1.2] tracking-[-0.02em] text-[#14150f]">
              How it works
            </h2>
          </motion.div>

          <div>
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                }}
                className="flex gap-5 border-t border-black/10 py-7"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#D6A039,#8A6415)] font-['Inter',sans-serif] text-[16px] font-semibold text-[#f5f6f2]">
                  {step.number}
                </div>

                <div>
                  <h3 className="font-['Poppins',sans-serif] text-[16.5px] font-semibold tracking-[-0.02em] text-[#14150f]">
                    {step.title}
                  </h3>

                  <p className="mt-2 text-[14px] leading-[1.6] text-[#68716d]">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}

      <section className="relative overflow-hidden bg-[linear-gradient(180deg,#F6EFDD_0%,#eef0eb_100%)] px-6 py-20 sm:px-8">
        <div className="pointer-events-none absolute left-1/2 top-[10px] -translate-x-1/2 font-['Poppins',sans-serif] text-[150px] font-bold leading-none text-[#D6A039] opacity-[0.14]">
          &ldquo;
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative z-10 mx-auto max-w-[780px] text-center"
        >
          <blockquote className="font-['Poppins',sans-serif] text-[clamp(18px,2.4vw,24px)] font-medium leading-[1.5] text-[#14150f]">
            &ldquo;The hardest part wasn&apos;t the skills gap — it was getting past the first
            screen. Someone had to vouch for how founder experience actually translates.&rdquo;
          </blockquote>

          <cite className="mt-6 block font-['JetBrains_Mono',monospace] text-[12.5px] not-italic text-[#68716d]">
            — A common theme we hear from ex-founders in this program
          </cite>
        </motion.div>
      </section>

      {/* FINAL CTA */}

      <section className="bg-[linear-gradient(120deg,#14150f,#8A6415)] px-6 py-[96px] text-center sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-[1000px]"
        >
          {/* HEADING */}
          <h2 className="font-['Poppins',sans-serif] text-[clamp(24px,3vw,34px)] font-semibold leading-[1.2] tracking-[-0.02em] text-[#f5f6f2]">
            Ready to find your next operating role?
          </h2>

          {/* DESCRIPTION */}
          <p className="mx-auto mt-5 max-w-[720px] text-[15.5px] leading-[1.6] text-white/70">
            Submit your profile and tell us what you built — we'll help position it for what's next.
          </p>

          {/* BUTTON */}
          <div className="mt-9">
            <button
              type="button"
              onClick={openModal}
              className="inline-flex items-center justify-center bg-[#f5f6f2] px-[26px] py-[15px] font-['Inter',sans-serif] text-[15px] font-semibold text-[#8A6415] transition-colors duration-200 hover:bg-white"
            >
              Submit your profile →
            </button>
          </div>
        </motion.div>
      </section>
      {/* PROFILE MODAL */}

      {/* PROFILE MODAL */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(20,21,15,0.55)] p-6"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeModal();
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 14, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 14, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="profileModalTitle"
              onMouseDown={(event) => event.stopPropagation()}
              className="
          relative
          w-full
          max-w-[520px]
          max-h-[88vh]
          overflow-y-auto
          rounded-[6px]
          bg-[#f5f6f2]
          px-5
          py-7
          leading-[normal]
          shadow-[0_24px_64px_rgba(20,21,15,0.28)]
          sm:p-9
        "
            >
              {/* CLOSE BUTTON */}
              <button
                type="button"
                onClick={closeModal}
                disabled={submitting}
                aria-label="Close"
                className="
            absolute
            right-[18px]
            top-[18px]
            flex
            h-8
            w-8
            items-center
            justify-center
            rounded-full
            bg-[#eef0eb]
            text-[#59605a]
            transition
            hover:bg-[#16833c]/15
            hover:text-[#16833c]
          "
              >
                <X size={15} strokeWidth={1.8} />
              </button>

              {!isSubmitted ? (
                <>
                  {/* EYEBROW */}
                  <span className="block font-['JetBrains_Mono',monospace] text-[11px] uppercase tracking-[0.04em] text-[#356c4b]">
                    // For Ex-Founders
                  </span>

                  {/* TITLE */}
                  <h3
                    id="profileModalTitle"
                    className="mt-[10px] pr-[30px] font-['Poppins',sans-serif] text-[21px] font-semibold leading-[1.25] tracking-[-0.02em] text-[#14150f]"
                  >
                    Submit your profile
                  </h3>

                  {/* DESCRIPTION */}
                  <p className="mt-[6px] mb-[26px] text-[13.5px] text-[#68716d]">
                    Tell us what you can actually solve for a startup — that's what gets you in
                    front of the right hiring manager, not just a resume.
                  </p>

                  {/* FORM */}
                  <form onSubmit={handleSubmit}>
                    <div className="flex flex-col gap-4">
                      {/* NAME + EMAIL */}
                      <div className="grid grid-cols-1 gap-[14px] sm:grid-cols-2">
                        {/* NAME */}
                        <div className="flex flex-col gap-[6px]">
                          <label
                            htmlFor="profileName"
                            className="font-['JetBrains_Mono',monospace] text-[11px] uppercase tracking-[0.03em] text-[#68716d]"
                          >
                            Your name
                          </label>

                          <input
                            id="profileName"
                            type="text"
                            autoFocus
                            value={name}
                            onChange={(event) => {
                              setName(event.target.value);
                              setErrors((current) => ({
                                ...current,
                                name: undefined,
                              }));
                            }}
                            placeholder="Jordan Patel"
                            className={`
                        w-full
                        rounded-[3px]
                        border
                        bg-[#f5f6f2]
                        px-[13px]
                        py-[11px]
                        font-['Inter',sans-serif]
                        text-[14.5px]
                        text-[#14150f]
                        transition
                        placeholder:text-[#8a8f88]
                        focus:outline-none
                        focus:ring-2
                        focus:ring-[#1CAA3C]
                        focus:ring-offset-[3px]
                        focus:ring-offset-[#f5f6f2]
                        ${
                          errors.name
                            ? 'border-[#B3261E] focus:border-[#B3261E]'
                            : 'border-[rgba(20,21,15,0.10)] focus:border-[#0B5C22]'
                        }
                      `}
                          />

                          {errors.name && (
                            <p className="mt-[2px] text-[12.5px] text-red-600">{errors.name}</p>
                          )}
                        </div>

                        {/* EMAIL */}
                        <div className="flex flex-col gap-[6px]">
                          <label
                            htmlFor="profileEmail"
                            className="font-['JetBrains_Mono',monospace] text-[11px] uppercase tracking-[0.03em] text-[#68716d]"
                          >
                            Email
                          </label>

                          <input
                            id="profileEmail"
                            type="email"
                            value={email}
                            onChange={(event) => {
                              setEmail(event.target.value);
                              setErrors((current) => ({
                                ...current,
                                email: undefined,
                              }));
                            }}
                            placeholder="jordan@email.com"
                            className={`
                        w-full
                        rounded-[3px]
                        border
                        bg-[#f5f6f2]
                        px-[13px]
                        py-[11px]
                        font-['Inter',sans-serif]
                        text-[14.5px]
                        text-[#14150f]
                        transition
                        placeholder:text-[#8a8f88]
                        focus:outline-none
                        focus:ring-2
                        focus:ring-[#1CAA3C]
                        focus:ring-offset-[3px]
                        focus:ring-offset-[#f5f6f2]
                        ${
                          errors.email
                            ? 'border-[#B3261E] focus:border-[#B3261E]'
                            : 'border-[rgba(20,21,15,0.10)] focus:border-[#0B5C22]'
                        }
                      `}
                          />

                          {errors.email && (
                            <p className="mt-[2px] text-[12.5px] text-red-600">{errors.email}</p>
                          )}
                        </div>
                      </div>

                      {/* LINKEDIN */}
                      <div className="flex flex-col gap-[6px]">
                        <label
                          htmlFor="profileLinkedin"
                          className="font-['JetBrains_Mono',monospace] text-[11px] uppercase tracking-[0.03em] text-[#68716d]"
                        >
                          LinkedIn URL
                        </label>

                        <input
                          id="profileLinkedin"
                          type="url"
                          value={linkedin}
                          onChange={(event) => {
                            setLinkedin(event.target.value);
                            setErrors((current) => ({
                              ...current,
                              linkedin: undefined,
                            }));
                          }}
                          placeholder="linkedin.com/in/yourname"
                          className={`
                      w-full
                      rounded-[3px]
                      border
                      bg-[#f5f6f2]
                      px-[13px]
                      py-[11px]
                      font-['Inter',sans-serif]
                      text-[14.5px]
                      text-[#14150f]
                      transition
                      placeholder:text-[#8a8f88]
                      focus:outline-none
                      focus:ring-2
                      focus:ring-[#1CAA3C]
                      focus:ring-offset-[3px]
                      focus:ring-offset-[#f5f6f2]
                      ${
                        errors.linkedin
                          ? 'border-[#B3261E] focus:border-[#B3261E]'
                          : 'border-[rgba(20,21,15,0.10)] focus:border-[#0B5C22]'
                      }
                    `}
                        />

                        {errors.linkedin && (
                          <p className="mt-[2px] text-[12.5px] text-red-600">
                            {errors.linkedin}
                          </p>
                        )}
                      </div>

                      {/* PITCH */}
                      <div className="flex flex-col gap-[6px]">
                        <label
                          htmlFor="profilePitch"
                          className="font-['JetBrains_Mono',monospace] text-[11px] uppercase tracking-[0.03em] text-[#68716d]"
                        >
                          What can you solve for a startup?
                        </label>

                        <textarea
                          id="profilePitch"
                          value={pitch}
                          onChange={(event) => {
                            setPitch(event.target.value);
                            setErrors((current) => ({
                              ...current,
                              pitch: undefined,
                            }));
                          }}
                          placeholder="e.g. I can help a Series A startup scale revenue — I was Founder & CRO and scaled my company to $5M ARR."
                          className={`
                      min-h-[84px]
                      w-full
                      resize-y
                      rounded-[3px]
                      border
                      bg-[#f5f6f2]
                      px-[13px]
                      py-[11px]
                      font-['Inter',sans-serif]
                      text-[14.5px]
                      text-[#14150f]
                      transition
                      placeholder:text-[#8a8f88]
                      focus:outline-none
                      focus:ring-2
                      focus:ring-[#1CAA3C]
                      focus:ring-offset-[3px]
                      focus:ring-offset-[#f5f6f2]
                      ${
                        errors.pitch
                          ? 'border-[#B3261E] focus:border-[#B3261E]'
                          : 'border-[rgba(20,21,15,0.10)] focus:border-[#0B5C22]'
                      }
                    `}
                        />

                        {errors.pitch && (
                          <p className="mt-[2px] text-[12.5px] text-red-600">{errors.pitch}</p>
                        )}
                      </div>

                      {/* FILE */}
                      <div className="flex flex-col gap-[6px]">
                        <label
                          htmlFor="profileFile"
                          className="font-['JetBrains_Mono',monospace] text-[11px] uppercase tracking-[0.03em] text-[#68716d]"
                        >
                          Attach your profile{' '}
                          <span className="normal-case tracking-normal text-[#8a8f88]">
                            (PDF, DOC or DOCX optional)
                          </span>
                        </label>

                        <input
                          id="profileFile"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(event) => {
                            setFile(event.target.files?.[0] ?? null);
                          }}
                          className="
                      w-full
                      rounded-[3px]
                      border
                      border-[rgba(20,21,15,0.10)]
                      bg-[#f5f6f2]
                      px-[10px]
                      py-[9px]
                      font-['Inter',sans-serif]
                      text-[13px]
                      text-[#59605a]
                      transition
                      focus:border-[#0B5C22]
                      focus:outline-none
                      focus:ring-2
                      focus:ring-[#1CAA3C]
                      focus:ring-offset-[3px]
                      focus:ring-offset-[#f5f6f2]
                      file:mr-3
                      file:border
                      file:border-[#9fa49e]
                      file:bg-[#f1f2ef]
                      file:px-3
                      file:py-1
                      file:text-[13px]
                      file:text-[#343a35]
                    "
                        />
                      </div>

                      {/* SUBMIT */}
                      {submitError && <p role="alert" className="text-[12.5px] text-red-600">{submitError}</p>}
                      <button
                        type="submit"
                        disabled={submitting}
                        aria-busy={submitting}
                        className="
                    mt-[6px]
                    w-full
                    rounded-[3px]
                    bg-[#209735]
                    px-5
                    py-[14px]
                    font-['Inter',sans-serif]
                    text-[14.5px]
                    font-semibold
                    text-white
                    transition
                    hover:bg-[#16833c]
                  "
                      >
                        {submitting ? 'Submitting…' : 'Submit your profile →'}
                      </button>

                      {/* NOTE */}
                      <p className="mt-[2px] font-['JetBrains_Mono',monospace] text-[11px] text-[#68716d]">
                        We'll review and reach out if there's a fit for a live search.
                      </p>
                    </div>
                  </form>
                </>
              ) : (
                /* SUCCESS STATE */
                <div className="py-5 text-center">
                  <div className="mx-auto flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#dff4e4]">
                    <Check size={22} className="text-[#0b5c22]" />
                  </div>

                  <h3 className="mt-[18px] font-['Poppins',sans-serif] text-[19px] font-semibold tracking-[-0.02em] text-[#14150f]">
                    Profile received.
                  </h3>

                  <p className="mx-auto mt-2 max-w-[360px] text-[14px] leading-[1.6] text-[#68716d]">
                    We'll review your background and reach out if there's a fit for a role we're
                    actively working on.
                  </p>

                  <button
                    type="button"
                    onClick={closeModal}
                    className="
                mt-7
                rounded-[3px]
                bg-[#209735]
                px-7
                py-3
                text-sm
                font-semibold
                text-white
                transition
                hover:bg-[#16833c]
              "
                  >
                    Close
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};